<script setup lang="ts">
const props = defineProps<{
  error: {
    statusCode?: number;
    statusMessage?: string;
    message?: string;
  };
}>();

const { t } = useI18n();
const localeRoute = useLocaleRoute();

const statusCode = computed(() => props.error.statusCode || 500);

const errorType = computed(() => {
  if (statusCode.value === 404) return "404";
  if (statusCode.value === 403) return "403";
  if (statusCode.value === 429) return "429";

  return "500";
});

const title = computed(() => t(`error_page.${errorType.value}.title`));

const description = computed(() =>
  t(`error_page.${errorType.value}.description`),
);

useHead(() => ({
  title: title.value,
  meta: [
    {
      name: "description",
      content: description.value,
    },
    {
      name: "robots",
      content: "noindex, nofollow",
    },
    {
      property: "og:type",
      content: "website",
    },
    {
      property: "og:title",
      content: title.value,
    },
    {
      property: "og:description",
      content: description.value,
    },
  ],
}));

function goHome() {
  clearError({
    redirect: localeRoute("/")?.fullPath || "/",
  });
}

function goBack() {
  if (window.history.length > 1) {
    clearError();
    window.history.back();
    return;
  }

  goHome();
}
</script>

<template>
  <main
    class="flex min-h-dvh w-full flex-col items-center justify-center bg-white px-4"
  >
    <div class="flex w-full max-w-[400px] flex-col items-center text-center">
      <div
        class="select-none text-[104px] font-bold text-primary sm:text-[140px]"
      >
        {{ statusCode }}
      </div>

      <h1 class="-mt-2 text-3xl font-semibold text-app-text">
        {{ title }}
      </h1>

      <p class="mt-3 text-base font-medium text-app-muted">
        {{ description }}
      </p>

      <div class="mt-8 flex w-full flex-col gap-3">
        <button
          type="button"
          class="flex w-full select-none items-center justify-center rounded-lg bg-primary py-2.5 text-base font-semibold text-white transition hover:bg-primary-400"
          @click="goHome"
        >
          {{ t("error_page.actions.go_home") }}
        </button>

        <button
          type="button"
          class="flex w-full select-none items-center justify-center rounded-lg border border-app-border bg-white py-2.5 text-base font-semibold text-app-text transition hover:bg-gray-100"
          @click="goBack"
        >
          {{ t("error_page.actions.go_back") }}
        </button>
      </div>
    </div>
  </main>
</template>
